import React, { useCallback, useEffect, useState } from 'react';
import { Button, EmptyState, Skeleton, fmtNum } from '../components/ui';

// Публичная страница команды rublox.pro/team.
// Тянет список членов команды из team-микросервиса (публичный эндпоинт).
// Если хочешь подать заявку — кнопка ведёт на team.rublox.pro/join.

const TEAM_API = (typeof window !== 'undefined' &&
                  window.location.hostname === 'localhost')
  ? 'http://localhost:8686'
  : 'https://team.rublox.pro';

const ROLE_RU = {
  owner:        'Хозяин',
  maintainer:   'Хранитель',
  senior_dev:   'Старший разработчик',
  trusted_dev:  'Доверенный разработчик',
  contributor:  'Участник',
  designer:     'Дизайнер',
  tester:       'Тестировщик',
  idea_only:    'Идейный',
  observer:     'Наблюдатель',
};

const ROLE_ORDER = ['owner', 'maintainer', 'senior_dev', 'trusted_dev',
                    'designer', 'tester', 'contributor', 'idea_only', 'observer'];

export default function TeamPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  /* На rublox.pro мы не залогинены на team — запрос идёт без JWT и упирается
     в `role_required('any')`. Для публичной отдачи нужен открытый эндпоинт.

     РАНЬШЕ при любой неудаче подставлялся фиктивный состав:
     `setItems([{ user_id: 1, team_role: 'owner' }])`. То есть страница
     УТВЕРЖДАЛА, что в команде один Хозяин, хотя на деле просто не смогла
     получить данные. Выдумывать содержимое вместо признания ошибки нельзя:
     посетитель видит правдоподобную ложь и не может её распознать.
     Теперь неудача — это состояние ошибки с кнопкой «Повторить». */
  const load = useCallback(() => {
    setLoading(true);
    setError(false);
    fetch(`${TEAM_API}/api-team/members`, { mode: "cors" })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("closed"))))
      .then((r) => {
        if (r && Array.isArray(r.items)) setItems(r.items);
        else throw new Error("bad_shape");
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const grouped = ROLE_ORDER.map(r => ({
    role: r,
    members: items.filter(m => m.team_role === r),
  })).filter(g => g.members.length > 0);

  return (
    <div style={{
      minHeight: '100vh',
      background: '#0f1115',
      color: '#e7eaf0',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    }}>
      <div style={{maxWidth: 960, margin: '0 auto', padding: '60px 24px'}}>
        <a href="/" style={{color: '#6aa3ff', textDecoration: 'none'}}>← rublox.pro</a>

        <h1 style={{fontSize: 36, fontWeight: 800, margin: '24px 0 8px'}}>
          Команда Рублокса
        </h1>
        <p style={{color: '#8a93a4', fontSize: 16, marginBottom: 40}}>
          Это люди, которые делают Рублокс. Опенсорс-сообщество разработчиков,
          дизайнеров и идеологов.
        </p>

        <div style={{
          background: 'linear-gradient(135deg, #1c2030, #232b3d)',
          border: '1px solid #2c3344',
          borderRadius: 12,
          padding: 24,
          marginBottom: 40,
        }}>
          <h2 style={{margin: '0 0 8px', fontSize: 22}}>Хочешь помочь?</h2>
          <p style={{margin: '0 0 16px', color: '#b8c0d0'}}>
            Мы открываем код плееров под лицензией MIT.
            Принимаем разработчиков, дизайнеров, тестировщиков и просто
            людей с хорошими идеями. Возраст — 18+.
          </p>
          <a href="https://team.rublox.pro/join"
             style={{
               display: 'inline-block',
               background: '#3357ff', color: '#fff',
               padding: '10px 22px', borderRadius: 6,
               textDecoration: 'none', fontWeight: 600,
             }}>
            Подать заявку
          </a>
        </div>

        {loading ? (
          <div style={{ display: "grid", gap: 12 }}>
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} h={64} r={10} />
            ))}
          </div>
        ) : error ? (
          <EmptyState
            title="Не получилось загрузить команду"
            desc="Список временно недоступен. Попробуй обновить через минуту."
            action={
              <Button variant="primary" onClick={load}>
                Повторить
              </Button>
            }
          />
        ) : grouped.length === 0 ? (
          <EmptyState
            title="Пока никого нет"
            desc="Состав команды скоро появится здесь."
          />
        ) : (
          grouped.map(g => (
            <div key={g.role} style={{marginBottom: 32}}>
              <h2 style={{
                fontSize: 13, color: '#6b748a',
                textTransform: 'uppercase', letterSpacing: '0.06em',
                margin: '0 0 12px',
              }}>
                {ROLE_RU[g.role]} <span className="ui-num">({fmtNum(g.members.length)})</span>
              </h2>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                gap: 12,
              }}>
                {g.members.map(m => (
                  <div key={m.user_id} style={{
                    background: '#161922',
                    border: '1px solid #232838',
                    borderRadius: 10,
                    padding: 14,
                    textAlign: 'center',
                  }}>
                    <div style={{
                      width: 56, height: 56, borderRadius: '50%',
                      background: m.team_role === 'owner'
                        ? 'linear-gradient(135deg, #7a5cff, #3357ff)'
                        : '#3357ff',
                      margin: '0 auto 8px',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: '#fff', fontWeight: 700, fontSize: 16,
                    }}>
                      #{m.user_id}
                    </div>
                    <div style={{fontWeight: 600, fontSize: 14}}>
                      {m.user_id === 1 ? 'МИН' : `#${m.user_id}`}
                    </div>
                    {m.city && (
                      <div style={{color: '#8a93a4', fontSize: 12, marginTop: 2}}>
                        {m.city}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))
        )}

        <div style={{
          marginTop: 48,
          padding: 24,
          background: '#161922',
          border: '1px solid #232838',
          borderRadius: 12,
        }}>
          <h2 style={{margin: '0 0 12px', fontSize: 20}}>Открытый код</h2>
          <p style={{color: '#b8c0d0', margin: '0 0 12px'}}>
            Клиенты Рублокса разрабатываются открыто. Любой может посмотреть код,
            предложить улучшение, исправить баг.
          </p>
          <div style={{display: 'flex', gap: 12, flexWrap: 'wrap'}}>
            <a href="https://gitea.rublox.pro/rublox/rublox-web-player"
               style={{
                 padding: '8px 16px',
                 background: '#1c2030',
                 border: '1px solid #2c3344',
                 borderRadius: 6,
                 color: '#e7eaf0',
                 textDecoration: 'none',
               }}>
              Web-плеер (Babylon.js) ↗
            </a>
            <a href="https://gitea.rublox.pro/rublox/rublox-android-player"
               style={{
                 padding: '8px 16px',
                 background: '#1c2030',
                 border: '1px solid #2c3344',
                 borderRadius: 6,
                 color: '#e7eaf0',
                 textDecoration: 'none',
               }}>
              APK-плеер (Kotlin + Filament) ↗
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
